/**
 * Verrou d'accès à l'application.
 *
 * Même règle que Karl : le mot de passe est le mot « Ulrich » suivi de la date
 * du jour au format ddmmyyyy. Il change donc chaque jour sans rien à retenir.
 *
 * ── CE QUE CE VERROU PROTÈGE, ET CE QU'IL NE PROTÈGE PAS ──────────────────
 *
 * Karl garde ses données sur un serveur : son verrou est côté serveur, et sans
 * mot de passe l'API ne rend rien. Corridor FX est local-first (AD-1). Les
 * devis, les profils et le livre vivent dans l'IndexedDB de l'appareil, et
 * l'application s'ouvre sans réseau. Le verrou ne peut donc être que côté
 * client, et il faut dire honnêtement ce qu'il vaut :
 *
 *   il empêche quelqu'un qui prend votre téléphone déverrouillé, ou qui passe
 *   devant votre écran, d'ouvrir l'application et de lire vos taux, vos marges
 *   et vos notes clients ;
 *
 *   il n'empêche PAS quelqu'un qui maîtrise les outils de développement du
 *   navigateur de lire la base directement. Aucun verrou en JavaScript ne le
 *   pourrait : le code s'exécute sur la machine de celui qu'il prétend
 *   arrêter.
 *
 * Le prétendre plus fort serait pire que de ne rien mettre, parce qu'on y
 * confierait des données qu'on n'y mettrait pas autrement. Ce qui protège
 * vraiment ces données, c'est le verrouillage de l'appareil.
 *
 * La synchronisation, elle, est gardée par un vrai secret côté serveur : le
 * jeton d'appareil (AD-8). Ce sont deux protections distinctes.
 *
 * Module pur : aucun accès au stockage ni au DOM, donc testable sans navigateur.
 */

/** Mot de passe attendu pour une date donnée. */
export function motDePasseDuJour(date = new Date()) {
  const jj = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `Ulrich${jj}${mm}${date.getFullYear()}`;
}

/**
 * La saisie est-elle correcte ?
 *
 * Comparaison insensible aux espaces de bord : un clavier de téléphone ajoute
 * volontiers une espace après un mot, et refuser pour cela serait
 * incompréhensible. La casse, elle, compte - c'est un mot de passe.
 */
export function verifier(saisie, date = new Date()) {
  return String(saisie ?? '').trim() === motDePasseDuJour(date);
}

/**
 * Jusqu'à quand une session ouverte reste valable : minuit.
 *
 * Le mot de passe change à minuit, la session doit expirer au même instant.
 * La laisser courir 24 h glissantes ferait vivre une session ouverte hier
 * jusqu'à demain, et le verrou quotidien perdrait son sens.
 */
export function finDeSession(date = new Date()) {
  const minuit = new Date(date);
  minuit.setHours(24, 0, 0, 0);
  return minuit.getTime();
}

/** Une session mémorisée est-elle encore valable ? */
export function sessionValide(session, date = new Date()) {
  return Boolean(session) && Number(session.expire) > date.getTime();
}

/** Session à mémoriser après une saisie correcte. */
export function ouvrirSession(date = new Date()) {
  return { expire: finDeSession(date) };
}

/**
 * Temporisation après des saisies erronées.
 *
 * Elle ne protège de rien contre quelqu'un qui lit le code - le mot de passe
 * s'y trouve. Elle décourage la saisie à l'aveugle par un curieux, ce qui est
 * exactement le périmètre de ce verrou. Progressive et bornée : bloquer plus
 * d'une minute punirait surtout le propriétaire qui se trompe de date.
 */
export const ESSAIS_AVANT_ATTENTE = 3;

export function attenteSecondes(essais) {
  if (essais < ESSAIS_AVANT_ATTENTE) return 0;
  return Math.min(60, 5 * 2 ** (essais - ESSAIS_AVANT_ATTENTE));
}
